import { Fragment, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWidgetStore, WidgetOrchestratorContext } from '../../state/createWidgetStore';
import { MessageBubble } from './../MessageBubble.tsx';
import { renderChart } from '../../utils/chart-renderer';
import { schemaToFields } from '../../utils/form-renderer';
import { t } from '../../i18n';
import type { ChatMessage, McpToolDef } from '../../types';
import { filterValidTools } from '../../utils/tool-validation';
import {
  buildAiFormFields,
  buildAiFormMessage,
  buildChipMessage,
  getAiFormPersistenceKey,
  getPromptSuggestions,
  parseFormSpecJson,
  promptForMissingFieldsAsText,
} from './MessageList.helpers';
import {
  enableAutoScroll,
  scrollToBottom,
  scrollToBottomIfEnabled,
} from '../../utils/scroll';
import type {
  AiFormPortalEntry,
  ChipFormState,
  ContentModalState,
  PromptSuggestionItem,
} from './MessageList.helpers';
import { McpForm } from '../form/McpForm';
import { MessageListChipForm } from './MessageListChipForm.tsx';
import { MessageListContentModal } from './MessageListContentModal.tsx';
import { MessageListSuggestionChips } from './MessageListSuggestionChips.tsx';

export interface MessageListProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  portalMode?: boolean;
}

/** How long the conversation must be idle before quick-action chips reveal/relocate. */
const IDLE_REVEAL_DELAY_MS = 1200;

export function MessageList({ scrollContainerRef, portalMode = false }: MessageListProps) {
  const {messages, tools, config, auth, drivingMode, isLoading} = useWidgetStore((state) => state);
  const orchestratorRefCtx = useContext(WidgetOrchestratorContext);
  const listRef = useRef<HTMLDivElement>(null);
  const savedFormValuesRef = useRef(new Map<string, Record<string, unknown>>());
  const closedAiFormsRef = useRef(new Set<string>());

  const [chipFormState, setChipFormState] = useState<ChipFormState | null>(null);
  const [modalContent, setModalContent] = useState<ContentModalState | null>(null);
  const [aiFormPortals, setAiFormPortals] = useState<AiFormPortalEntry[]>([]);

  const liveTranscript = drivingMode.transcript.trim();
  const renderedMessages: ChatMessage[] = liveTranscript
    ? [
        ...messages,
        {
          id: '__live-transcript__',
          role: 'user',
          content: liveTranscript,
          timestamp: Date.now(),
        },
      ]
    : messages;

  const activeToolSet = new Set([...(config.allowedTools || []), ...(config.dangerousTools || [])]);
  const chatTools: McpToolDef[] =
    config.allowedTools !== undefined || config.dangerousTools !== undefined
      ? filterValidTools(tools.filter((tool) => activeToolSet.has(tool.id)))
      : filterValidTools(tools);

  const isAuthenticated = auth.status === 'authenticated';
  const suggestions: PromptSuggestionItem[] = isAuthenticated
    ? chatTools.flatMap((tool) => getPromptSuggestions(tool))
    : [];

  let lastGreetingIdx = -1;
  for (let i = renderedMessages.length - 1; i >= 0; i -= 1) {
    if (renderedMessages[i].isGreeting) {
      lastGreetingIdx = i;
      break;
    }
  }

  if (lastGreetingIdx === -1 && renderedMessages[0]?.role === 'assistant') {
    lastGreetingIdx = 0;
  }


let lastQualifyingAssistantIdx = -1;
  // Only reveal the chips once the conversation has been idle for a bit rather than
  // re-anchoring the instant a new qualifying reply lands - otherwise the chip row
  // relocates on every assistant turn, which reads as constant jumping.
  const [debouncedQualifyingIdx, setDebouncedQualifyingIdx] = useState(-1);

  useEffect(() => {
    if (isLoading || lastQualifyingAssistantIdx < 0) {
      setDebouncedQualifyingIdx(-1);
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQualifyingIdx(lastQualifyingAssistantIdx);
    }, IDLE_REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading, lastQualifyingAssistantIdx]);

  const suggestionAnchorIdx = !chipFormState
    ? (debouncedQualifyingIdx >= 0 ? debouncedQualifyingIdx : lastGreetingIdx)
    : -1;

  useLayoutEffect(() => {
    scrollToBottomIfEnabled(scrollContainerRef.current);
  }, [messages, scrollContainerRef]);

  useEffect(() => {
    if (!listRef.current) return;

    const renderPendingCharts = () => {
      listRef.current
        ?.querySelectorAll('.chart-container:not(.chart-rendered):not(.chart-error)')
        .forEach((container) => renderChart(container as HTMLElement));
    };

    renderPendingCharts();

    const observer = new MutationObserver(() => {
      renderPendingCharts();
    });

    observer.observe(listRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    // Render chart placeholders before paint so the user doesn't see a
    // completed message sit in the "Loading chart..." state first.
    listRef.current
      ?.querySelectorAll('.chart-container:not(.chart-rendered):not(.chart-error)')
      .forEach((container) => renderChart(container as HTMLElement));
  }, [messages]);

  useLayoutEffect(() => {
    if (!listRef.current) return;

    const containers = listRef.current.querySelectorAll(
      '.form-container:not([data-form-portal-id]):not(.form-error):not(.form-closed)',
    );

    const newEntries: AiFormPortalEntry[] = [];
    containers.forEach((container) => {
      const el = container as HTMLElement;
      const raw = el.getAttribute('data-form-spec');
      if (!raw) return;

      try {
        const spec = parseFormSpecJson(raw);
        const built = buildAiFormFields(spec, tools, el, messages);
        const toolName = spec.tool as string | undefined;
        const key = getAiFormPersistenceKey(el, toolName);

        if (key && closedAiFormsRef.current.has(key)) {
          el.classList.add('form-closed');
          return;
        }

        if (!built) {
          el.textContent = 'No form fields defined';
          el.classList.add('form-error');
          return;
        }

        const portalId = `ai-form-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        el.setAttribute('data-form-portal-id', portalId);
        newEntries.push({
          id: portalId,
          containerEl: el,
          toolName: built.toolName,
          fields: built.fields,
          initialValues: key ? savedFormValuesRef.current.get(key) : undefined,
        });
      } catch (err) {
        console.error('[mcp-chat-widget] Failed to render AI form', err, raw);
        el.textContent = 'Invalid form data';
        el.classList.add('form-error');
      }
    });

    setAiFormPortals((prev) => {
      const pruned = prev.filter((p) => p.containerEl.isConnected);
      if (newEntries.length === 0 && pruned.length === prev.length) return prev;
      return [...pruned, ...newEntries];
    });
  }, [messages, tools]);

  // Portal DOM lands on the *next* commit after the state update above, so the
  // scroll has to happen in its own effect keyed on the portals themselves.
  useLayoutEffect(() => {
    if (aiFormPortals.length > 0) scrollToBottomIfEnabled(scrollContainerRef.current);
  }, [aiFormPortals.length, scrollContainerRef]);

  const handleAiFormSubmit = (entry: AiFormPortalEntry, values: Record<string, unknown>) => {
    if (auth.status !== 'authenticated' || isLoading) return;

    const key = getAiFormPersistenceKey(entry.containerEl, entry.toolName);
    if (key) {
      savedFormValuesRef.current.set(key, { ...values });
      closedAiFormsRef.current.add(key);
    }

    orchestratorRefCtx?.current?.sendMessage(buildAiFormMessage(entry.toolName, values, entry.fields));
    entry.containerEl.classList.add('form-closed');
    setAiFormPortals((prev) => prev.filter((p) => p.id !== entry.id));
  };

  const handleAiFormCancel = (entry: AiFormPortalEntry) => {
    const key = getAiFormPersistenceKey(entry.containerEl, entry.toolName);
    if (key) closedAiFormsRef.current.add(key);
    entry.containerEl.classList.add('form-closed');
    setAiFormPortals((prev) => prev.filter((p) => p.id !== entry.id));
  };

  const handleListClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    const imageBtn = target.closest('.user-attachment-image') as HTMLElement | null;
    if (imageBtn) {
      const imageUrl = imageBtn.getAttribute('data-image-url');
      if (imageUrl) {
        setModalContent({
          type: 'image',
          imageUrl,
          imageAlt: imageBtn.getAttribute('data-image-alt') || undefined,
        });
      }
      return;
    }

    const expandBtn = target.closest('.expand-icon') as HTMLElement | null;
    if (expandBtn) {
      event.stopPropagation();
      const expandType = expandBtn.dataset.expand;
      const parent = expandBtn.parentElement;
      if (!parent || !expandType) return;

      if (expandType === 'chart') {
        const spec = parent.getAttribute('data-chart-spec');
        if (spec) setModalContent({ type: 'chart', chartSpec: spec });
      } else if (expandType === 'table') {
        const table = parent.querySelector('.md-table');
        if (table) {
          setModalContent({ type: 'table', tableHtml: (table as HTMLElement).outerHTML });
        }
      }
      return;
    }
  };

  const handleSuggestionChip = (suggestion: PromptSuggestionItem) => {
    if (!isAuthenticated || isLoading) return;

    const scrollContainer = scrollContainerRef.current;
    enableAutoScroll(scrollContainer);
    scrollToBottom(scrollContainer);

    const toolDef = tools.find((tool) => tool.name === suggestion.toolName);
    const requiredFields = suggestion.requiredFields;
    const hasRequiredFields = !!requiredFields && requiredFields.length > 0;

    if (hasRequiredFields && toolDef?.formEnabled === false) {
      promptForMissingFieldsAsText(suggestion, config.language);
      return;
    }

    if (requiredFields && hasRequiredFields && toolDef?.formEnabled !== false) {
      if (!toolDef?.inputSchema) {
        orchestratorRefCtx?.current?.sendMessage(suggestion.text);
        return;
      }

      const fullProps = (toolDef.inputSchema as Record<string, unknown>).properties as
        | Record<string, unknown>
        | undefined;
      if (!fullProps) {
        orchestratorRefCtx?.current?.sendMessage(suggestion.text);
        return;
      }

      const filteredSchema: Record<string, unknown> = {
        type: 'object',
        properties: {} as Record<string, unknown>,
        required: requiredFields,
      };

      for (const fieldName of requiredFields) {
        if (fullProps[fieldName]) {
          (filteredSchema.properties as Record<string, unknown>)[fieldName] =
            fullProps[fieldName];
        }
      }

      const fields = schemaToFields(filteredSchema, {});
      if (fields.length === 0) {
        orchestratorRefCtx?.current?.sendMessage(suggestion.text);
        return;
      }

      for (const field of fields) {
        field.required = true;
        if (suggestion.labels?.[field.name]) {
          field.label = suggestion.labels[field.name];
        }
      }

      setChipFormState({
        toolName: suggestion.toolName,
        chipText: suggestion.text,
        labels: suggestion.labels,
        fields,
      });
      return;
    }

    orchestratorRefCtx?.current?.sendMessage(suggestion.text);
  };

  return (
    <>
      <div
        ref={listRef}
        className="message-list"
        role="log"
        aria-live="polite"
        onClick={handleListClick}
      >
        {renderedMessages.map((msg, index) => (
          <Fragment key={msg.id}>
            <MessageBubble msg={msg} portalMode={portalMode} />
            {suggestions.length > 0 && index === suggestionAnchorIdx && (
              <MessageListSuggestionChips
                suggestions={suggestions}
                onSelect={handleSuggestionChip}
                disabled={index === lastQualifyingAssistantIdx ? isLoading : undefined}
              />
            )}
          </Fragment>
        ))}
        {chipFormState && (
          <MessageListChipForm
            state={chipFormState}
            onSubmit={(values) => {
              orchestratorRefCtx?.current?.sendMessage(
                buildChipMessage(chipFormState.chipText, values, chipFormState.fields),
              );
              setChipFormState(null);
            }}
            onCancel={() => setChipFormState(null)}
          />
        )}
        {aiFormPortals.map((entry) => createPortal(
          <McpForm
            fields={entry.fields}
            initialValues={entry.initialValues}
            submitLabel={t('formSubmit')}
            cancelLabel={t('formCancel')}
            onCancel={() => handleAiFormCancel(entry)}
            onSubmit={(values) => handleAiFormSubmit(entry, values)}
            formType="ai"
            toolName={entry.toolName}
          />,
          entry.containerEl,
          entry.id,
        ))}
      </div>
      {modalContent && (
        <MessageListContentModal
          state={modalContent}
          onClose={() => setModalContent(null)}
        />
      )}
    </>
  );
}
